import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fmtCurrency } from '../utils/tradePayData';
import { loadPublishedWageRatesOnce, unionScaleForPayTrade, PAY_TRADE_UNION_SCALE, fmtRate } from '../utils/unionWages';
import useIsPhone from '../utils/useIsPhone';

const NAVY      = '#0D1B2A';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";
const HELPER    = 'rgba(255,255,255,0.72)';

// "Union scale in the Chicago area": the government median for this trade beside the published
// union journeyman hourly wage (one figure, or the range across matching locals). Additive block
// under the Career Pay Guide pay bar. Renders nothing for a trade with no clear union match
// (see PAY_TRADE_UNION_SCALE), while loading, or if the wage rows can't be read.
//
// The union figure is the hourly WAGE only -- never the total package -- and the median is
// labelled as a separate government statistic, so the two are never presented as one number.
// stacked: force the one-column layout (narrow containers at any viewport).
// compact: the version used inside a Career Pay Guide compare column -- sentence-case labels and
// smaller figures that wrap instead of overflowing, so everything stays inside the border at any
// column width. Text stays at least 14px on phones.
// largeText: with compact, keep the phone text sizes (14px and up) at every width -- used by the
// trade quiz results, which aren't squeezed into compare columns.
export default function UnionScaleBlock({ row, medianLabel, stacked = false, compact = false, largeText = false }) {
  const { t } = useTranslation();
  const isPhone = useIsPhone() || largeText;
  const mapped = !!PAY_TRADE_UNION_SCALE[row.trade];
  const [scale, setScale] = useState(null);

  useEffect(() => {
    if (!mapped) { setScale(null); return undefined; }
    let cancelled = false;
    loadPublishedWageRatesOnce().then(res => {
      if (cancelled) return;
      if (res.error) { console.warn('[union scale] wage rows not loaded:', res.error); setScale(null); return; }
      setScale(unionScaleForPayTrade(res.rows, row.trade));
    });
    return () => { cancelled = true; };
  }, [row.trade, mapped]);

  if (!scale) return null;
  const oneCol = isPhone || stacked;
  const tilde = scale.anyEstimate ? '~' : '';
  const wage = scale.min === scale.max
    ? `${tilde}${fmtRate(scale.min)}`
    : `${tilde}${fmtRate(scale.min)} – ${tilde}${fmtRate(scale.max)}`;
  const median = fmtCurrency(row.median_pay);
  // Compact sizes: phones keep 14px text; desktop compare columns use smaller, sentence-case text.
  const small = isPhone ? '0.875rem' : '0.78rem';
  const figureLabel = compact
    ? { fontSize: small, fontWeight: 600, color: HELPER, lineHeight: 1.35, marginBottom: '2px' }
    : { fontSize: oneCol ? '0.8rem' : '0.72rem', fontWeight: 700, color: HELPER, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '4px' };
  const bigNumber = compact
    ? { fontFamily: FONT_HEAD, fontSize: isPhone ? '1.9rem' : '1.45rem', color: WHITE, letterSpacing: '0.02em', lineHeight: 1.1,
        display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: '4px' }
    : { fontFamily: FONT_HEAD, fontSize: oneCol ? '2.2rem' : '1.9rem', color: WHITE, letterSpacing: '0.02em', lineHeight: 1.05 };
  const unit = compact
    ? { fontFamily: FONT_BODY, fontSize: isPhone ? '0.9rem' : '0.8rem', color: HELPER }
    : { fontFamily: FONT_BODY, fontSize: oneCol ? '0.95rem' : '0.85rem', color: HELPER, marginLeft: '4px' };
  const bodySize = compact ? small : (oneCol ? '0.875rem' : '0.78rem');

  return (
    <section data-union-scale data-compact={compact || undefined} aria-label={t('resources.unionScale.title')} style={{
      backgroundColor: NAVY, border: '1px solid rgba(245,166,35,0.3)', borderRadius: '10px',
      padding: compact ? '12px' : oneCol ? '16px' : '16px 18px', marginBottom: '20px', width: '100%',
      ...(compact ? { minWidth: 0, overflowWrap: 'anywhere' } : {}),
    }}>
      <p style={compact
        ? { fontSize: small, fontWeight: 700, color: AMBER, lineHeight: 1.35, marginBottom: '10px' }
        : { fontSize: oneCol ? '0.8rem' : '0.7rem', fontWeight: 700, color: AMBER, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '12px' }}>
        {t('resources.unionScale.title')}
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: oneCol ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))', gap: compact ? '10px' : oneCol ? '14px' : '18px' }}>
        {median && (
          <div data-gov-median>
            <p style={figureLabel}>{t('resources.unionScale.governmentMedian', { label: medianLabel })}</p>
            <p style={bigNumber}>{median}<span style={unit}>{t('resources.unionScale.perYear')}</span></p>
          </div>
        )}
        <div data-union-wage>
          <p style={figureLabel}>{t('resources.unionScale.unionWage')}</p>
          <p style={bigNumber}>{wage}<span style={unit}>{t('resources.unionScale.perHour')}</span></p>
          <p style={{ fontSize: compact ? small : oneCol ? '0.9rem' : '0.8rem', color: HELPER, marginTop: '4px' }}>
            {scale.classCount
              ? t('resources.unionScale.classesCount', { count: scale.classCount })
              : t('resources.unionScale.localsCount', { count: scale.locals.length })}
          </p>
        </div>
      </div>

      <p style={{ fontSize: bodySize, color: HELPER, lineHeight: 1.5, marginTop: compact ? '10px' : '12px' }}>
        {t(scale.allIdol ? 'resources.unionScale.wageNoteIdol' : scale.anyIdol ? 'resources.unionScale.wageNoteMixed' : 'resources.unionScale.wageNote')}
      </p>
      {scale.anyEstimate && (
        <p data-estimate-notice role="note" style={{
          fontSize: bodySize, color: 'rgba(255,255,255,0.8)', lineHeight: 1.5, marginTop: '10px',
          padding: '10px 12px', borderRadius: '8px', backgroundColor: 'rgba(245,166,35,0.08)', border: '1px solid rgba(245,166,35,0.25)',
        }}>
          {t('unionWages.estimateNotice')}
        </p>
      )}

      <Link
        to={`/union-wages?${new URLSearchParams({ family: scale.familyKey }).toString()}`}
        data-union-scale-link
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '44px',
          width: oneCol ? '100%' : 'fit-content', marginTop: compact ? '12px' : '14px', padding: compact ? '0 10px' : '0 18px',
          backgroundColor: AMBER, color: NAVY, borderRadius: '8px', textDecoration: 'none', textAlign: 'center',
          fontFamily: FONT_BODY, fontWeight: 700, fontSize: compact && !isPhone ? '0.85rem' : oneCol ? '0.95rem' : '0.85rem',
        }}
      >
        {t('resources.unionScale.seeUnionWages')}
      </Link>
    </section>
  );
}
